/**
 * BETohalics American Roulette — house table, wallet-native.
 * Server-authoritative: the browser only stages bet placements. On Spin, we
 * POST the full bet list; the server validates every bet, spins securely,
 * settles atomically, and returns the winning pocket + resolved bets. The
 * wheel animation always lands on the server-generated pocket.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
    ArrowLeft, Coins, History, HelpCircle, Undo2, RotateCcw, Volume2, VolumeX, Music, Repeat,
    Star, Save, X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCrapsAudio } from '../hooks/useCrapsAudio';
import HouseCrest from '../components/HouseCrest';
import WinFlash from '../components/WinFlash';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const RED_SET = new Set(['1','3','5','7','9','12','14','16','18','19','21','23','25','27','30','32','34','36']);
const colorOf = (n) => (n === '0' || n === '00') ? 'green' : (RED_SET.has(n) ? 'red' : 'black');

// American felt: 12 rows × 3 cols left-to-right (1,2,3 / 4,5,6 / ...)
const GRID_ROWS = [];
for (let r = 0; r < 12; r++) GRID_ROWS.push([`${3 * r + 1}`, `${3 * r + 2}`, `${3 * r + 3}`]);

const CHIP_COLORS = {
    1:   { bg: '#f5f5f5', fg: '#111', ring: '#cc0000' },
    5:   { bg: '#c02020', fg: '#fff', ring: '#f5e6b0' },
    10:  { bg: '#1e6cff', fg: '#fff', ring: '#cfe0ff' },
    25:  { bg: '#1fa845', fg: '#fff', ring: '#dbe8c9' },
    50:  { bg: '#e5a11a', fg: '#111', ring: '#8a5c00' },
    100: { bg: '#111', fg: '#f5c542', ring: '#f5c542' },
    500: { bg: '#7a2cff', fg: '#fff', ring: '#e0d0ff' },
};
const chipStyle = (v) => CHIP_COLORS[v] || CHIP_COLORS[1];

const Chip = ({ value, size = 28 }) => {
    const s = chipStyle(value);
    return (
        <div
            className="rounded-full flex items-center justify-center font-black shrink-0"
            style={{
                width: size, height: size,
                background: `radial-gradient(circle at 30% 30%, ${s.bg} 40%, #000 130%)`,
                border: `2px dashed ${s.ring}`,
                color: s.fg,
                fontSize: size <= 22 ? 8 : (size <= 30 ? 10 : 12),
                boxShadow: '0 2px 4px rgba(0,0,0,0.5)',
            }}
        >${value}</div>
    );
};

const BetStack = ({ amount }) => {
    if (!amount) return null;
    return (
        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center pointer-events-none">
            <div
                className="rounded-full flex items-center justify-center font-black text-white text-[9px]"
                style={{
                    width: 22, height: 22,
                    background: 'radial-gradient(circle at 30% 30%, #f0abfc 40%, #6b21a8 130%)',
                    border: '2px dashed #fff',
                    boxShadow: '0 2px 6px rgba(168, 85, 247, 0.6)',
                }}
            >${amount}</div>
        </div>
    );
};

const RouletteWheel = ({ wheelOrder, resultPocket, spinning, onSpinDone, onClack }) => {
    const [rotation, setRotation] = useState(0);
    const [ballRotation, setBallRotation] = useState(0);
    const [settled, setSettled] = useState(false);
    const [frozen, setFrozen] = useState(false);  // hold still for ~10s after winner lands
    const doneRef = useRef(false);
    const rotRef = useRef(0);       // running total wheel rotation
    const ballRotRef = useRef(0);   // running total ball rotation
    const freezeTimerRef = useRef(null);

    useEffect(() => {
        if (!spinning || !resultPocket) return;
        doneRef.current = false;
        setSettled(false);
        setFrozen(false);
        if (freezeTimerRef.current) { clearTimeout(freezeTimerRef.current); freezeTimerRef.current = null; }
        const idx = wheelOrder.indexOf(resultPocket);
        if (idx < 0) return;
        const anglePer = 360 / wheelOrder.length;
        // Snap current rotation to nearest full turn, then add spins + landing offset
        const spins = 6 + Math.floor(Math.random() * 2);      // 6-7 full turns
        const ballSpins = 9 + Math.floor(Math.random() * 2);  // 9-10 counter turns
        const landing = 360 - idx * anglePer;
        const currentTurns = Math.floor(rotRef.current / 360);
        const nextRot = (currentTurns + spins) * 360 + landing;
        // The ball is fixed to the top pointer visually — its rotation must
        // end at a whole multiple of 360° so it always settles under the
        // pointer, RIGHT ON TOP of the winning pocket (which the wheel has
        // just rotated into position).
        const currentBallTurns = Math.floor(Math.abs(ballRotRef.current) / 360);
        const nextBall = -((currentBallTurns + ballSpins) * 360);
        rotRef.current = nextRot;
        ballRotRef.current = nextBall;
        setRotation(nextRot);
        setBallRotation(nextBall);
        // Procedural "clacks" as the ball drops off the rim and bounces
        // between frets in the final ~0.6s of deceleration.
        const clackTimers = [
            setTimeout(() => onClack && onClack(), 4650),
            setTimeout(() => onClack && onClack(), 4880),
            setTimeout(() => onClack && onClack(), 5090),
            setTimeout(() => onClack && onClack(), 5240),
        ];
        const t = setTimeout(() => {
            if (doneRef.current) return;
            doneRef.current = true;
            setSettled(true);
            setFrozen(true);
            // Hold the wheel dead-still for 10s so the winning pocket sits
            // right under the pointer for everyone to admire. Then release
            // it back into the slow idle drift.
            freezeTimerRef.current = setTimeout(() => {
                setFrozen(false);
                freezeTimerRef.current = null;
            }, 10000);
            onSpinDone && onSpinDone();
        }, 5300);
        return () => { clearTimeout(t); clackTimers.forEach(clearTimeout); };
    }, [spinning, resultPocket, wheelOrder, onSpinDone, onClack]);

    // Clean up freeze timer on unmount
    useEffect(() => () => {
        if (freezeTimerRef.current) clearTimeout(freezeTimerRef.current);
    }, []);

    const anglePer = 360 / wheelOrder.length;

    return (
        <div className="relative aspect-square max-w-[380px] mx-auto" data-testid="roulette-wheel">
            <div className="absolute inset-0 rounded-full"
                 style={{
                     background: 'radial-gradient(circle at 50% 50%, #4a0a72 0%, #2a054a 60%, #100120 100%)',
                     boxShadow: '0 0 42px rgba(192, 132, 252, 0.35), inset 0 0 20px rgba(0,0,0,0.9)',
                     border: '4px solid #a855f7',
                 }} />
            <motion.div
                className="absolute inset-[8%] rounded-full origin-center"
                animate={{ rotate: spinning ? rotation : frozen ? rotation : rotation + 360 }}
                transition={
                    spinning
                        ? { duration: 5.2, ease: [0.14, 0.72, 0.24, 1] }
                        : frozen
                            ? { duration: 0 }
                            : { duration: 60, ease: 'linear', repeat: Infinity, repeatType: 'loop' }
                }
                style={{ background: '#2a054a', border: '2px solid #c084fc' }}
            >
                <svg viewBox="-100 -100 200 200" className="w-full h-full">
                    {wheelOrder.map((n, i) => {
                        const startAngle = i * anglePer - 90 - anglePer / 2;
                        const endAngle = startAngle + anglePer;
                        const sR = (startAngle * Math.PI) / 180;
                        const eR = (endAngle * Math.PI) / 180;
                        const r = 92;
                        const fill = colorOf(n) === 'green' ? '#0a8a3a' : (colorOf(n) === 'red' ? '#c11b1b' : '#111');
                        const labelAngle = i * anglePer - 90;
                        const lR = (labelAngle * Math.PI) / 180;
                        return (
                            <g key={`${n}-${i}`}>
                                <path
                                    d={`M0 0 L${r * Math.cos(sR)} ${r * Math.sin(sR)} A${r} ${r} 0 0 1 ${r * Math.cos(eR)} ${r * Math.sin(eR)} Z`}
                                    fill={fill} stroke="#c084fc" strokeWidth={0.4}
                                />
                                <text
                                    x={72 * Math.cos(lR)} y={72 * Math.sin(lR)}
                                    fill="#fff" fontSize="7" fontWeight="900"
                                    textAnchor="middle" dominantBaseline="central"
                                    style={{ transform: `rotate(${labelAngle + 90}deg)`, transformOrigin: `${72 * Math.cos(lR)}px ${72 * Math.sin(lR)}px` }}
                                >{n}</text>
                            </g>
                        );
                    })}
                    <circle cx="0" cy="0" r="18" fill="#4a0a72" stroke="#c084fc" strokeWidth="1.4" />
                    <circle cx="0" cy="0" r="6" fill="#c084fc" />
                </svg>
            </motion.div>
            <motion.div
                className="absolute inset-0 rounded-full pointer-events-none origin-center"
                animate={{ rotate: ballRotation }}
                transition={{ duration: 5.2, ease: [0.1, 0.85, 0.24, 1] }}
            >
                <div
                    className="absolute rounded-full bg-white"
                    style={{
                        width: 12, height: 12, top: '5%', left: '50%',
                        transform: 'translate(-50%, 0)',
                        boxShadow: '0 0 8px rgba(255,255,255,0.9), inset -1px -1px 3px rgba(0,0,0,0.3)',
                    }}
                />
            </motion.div>
            {/* Golden pulsing halo around the winning pocket — appears while the wheel is
                frozen (~10s after the ball settles) so players can savor the winning number */}
            <AnimatePresence>
                {frozen && resultPocket && (
                    <motion.div
                        key={`glow-${resultPocket}`}
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.6 }}
                        transition={{ duration: 0.4, ease: 'easeOut' }}
                        className="absolute pointer-events-none z-20"
                        style={{ top: '3.2%', left: '50%', transform: 'translate(-50%, 0)' }}
                        data-testid="roulette-winning-glow"
                    >
                        {/* Inner pulsing gold ring */}
                        <motion.div
                            className="rounded-full"
                            animate={{
                                boxShadow: [
                                    '0 0 22px 6px rgba(255,215,0,0.85), 0 0 44px 12px rgba(255,140,0,0.55), inset 0 0 10px rgba(255,215,0,0.7)',
                                    '0 0 32px 10px rgba(255,215,0,1), 0 0 62px 18px rgba(255,140,0,0.7), inset 0 0 14px rgba(255,215,0,0.9)',
                                    '0 0 22px 6px rgba(255,215,0,0.85), 0 0 44px 12px rgba(255,140,0,0.55), inset 0 0 10px rgba(255,215,0,0.7)',
                                ],
                            }}
                            transition={{ duration: 1.1, repeat: Infinity, ease: 'easeInOut' }}
                            style={{
                                width: 32, height: 32,
                                border: '3px solid #FFD700',
                                background: 'radial-gradient(circle, rgba(255,215,0,0.35) 0%, rgba(255,140,0,0.08) 55%, transparent 100%)',
                            }}
                        />
                        {/* Outer expanding gold rings — classic winner celebration */}
                        <motion.div
                            className="absolute inset-0 rounded-full"
                            animate={{ scale: [1, 2.2], opacity: [0.7, 0] }}
                            transition={{ duration: 1.4, repeat: Infinity, ease: 'easeOut' }}
                            style={{ border: '2px solid #FFD700' }}
                        />
                        <motion.div
                            className="absolute inset-0 rounded-full"
                            animate={{ scale: [1, 2.6], opacity: [0.5, 0] }}
                            transition={{ duration: 1.4, repeat: Infinity, ease: 'easeOut', delay: 0.5 }}
                            style={{ border: '2px solid #FFB800' }}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
            <div className="absolute left-1/2 -translate-x-1/2 -top-1 z-30 pointer-events-none">
                <div style={{
                    width: 0, height: 0,
                    borderLeft: '8px solid transparent',
                    borderRight: '8px solid transparent',
                    borderTop: '14px solid #c084fc',
                    filter: 'drop-shadow(0 2px 4px rgba(168, 85, 247, 0.6))',
                }} />
            </div>
        </div>
    );
};

const CELL_H = 48;   // px; matches Tailwind h-12
const GAP = 4;       // px; matches Tailwind gap-1
const HIT = 18;      // px; hit-zone thickness

const NumberCell = ({ n, amount, onClick, winner, highlight }) => {
    const col = colorOf(n);
    const bg = col === 'green' ? 'bg-[#7c3aed]' : (col === 'red' ? 'bg-[#c11b1b]' : 'bg-[#111]');
    return (
        <button
            onClick={onClick}
            className={`relative ${bg} text-white font-black text-sm md:text-base border border-[#c084fc]/70 hover:brightness-125 transition flex items-center justify-center rounded ${highlight ? 'ring-2 ring-[#f0abfc]' : ''} ${winner ? 'ring-4 ring-[#f0abfc] animate-pulse shadow-[0_0_18px_rgba(240,171,252,0.7)]' : ''}`}
            style={{ height: `${CELL_H}px` }}
            data-testid={`roulette-num-${n}`}
        >
            {n}
            <BetStack amount={amount} />
        </button>
    );
};

const LineChip = ({ amount }) => amount ? (
    <div
        className="rounded-full flex items-center justify-center font-black text-white text-[8px]"
        style={{
            width: 18, height: 18,
            background: 'radial-gradient(circle at 30% 30%, #f0abfc 40%, #6b21a8 130%)',
            border: '2px dashed #fff',
            boxShadow: '0 2px 6px rgba(168, 85, 247, 0.7)',
        }}
    >${amount}</div>
) : null;

/**
 * Hit-zone overlay layer — invisible click targets on the LINES between
 * numbers (splits, corners, streets, six-lines). Positioned absolutely over
 * the number grid using CSS calc from the known 3-column layout + 4px gap +
 * 48px cell height. Each hit-zone is ~18px thick so it's tappable on desktop
 * but not big enough to obscure the number cell itself.
 *
 * A subtle chip stack is rendered on the exact line/corner when a bet exists.
 */
const LineOverlays = ({ bets, onSplit, onCorner, onStreet, onSixLine, keyOf }) => {
    // horizontal split: between col c and c+1 within row r (c ∈ {0,1}, r ∈ 0..11)
    const hSplits = [];
    for (let r = 0; r < 12; r++) {
        for (let c = 0; c < 2; c++) {
            const a = String(3 * r + c + 1);
            const b = String(3 * r + c + 2);
            hSplits.push({ r, c, nums: [a, b] });
        }
    }
    // vertical split: between row r and r+1 in col c (c ∈ 0..2, r ∈ 0..10)
    const vSplits = [];
    for (let r = 0; r < 11; r++) {
        for (let c = 0; c < 3; c++) {
            const a = String(3 * r + c + 1);
            const b = String(3 * (r + 1) + c + 1);
            vSplits.push({ r, c, nums: [a, b] });
        }
    }
    // corner: at intersection of 4 numbers (top-left cell at row r, col c; c ∈ {0,1})
    const corners = [];
    for (let r = 0; r < 11; r++) {
        for (let c = 0; c < 2; c++) {
            const a = String(3 * r + c + 1);
            const b = String(3 * r + c + 2);
            const c1 = String(3 * (r + 1) + c + 1);
            const d = String(3 * (r + 1) + c + 2);
            corners.push({ r, c, nums: [a, b, c1, d] });
        }
    }
    // street: 3-number row (r ∈ 0..11) — hit zone on the LEFT edge of row 1
    const streets = [];
    for (let r = 0; r < 12; r++) {
        streets.push({ r, nums: [String(3 * r + 1), String(3 * r + 2), String(3 * r + 3)] });
    }
    // six-line: between row r and r+1, spans all 3 cols — hit zone on LEFT edge
    const sixLines = [];
    for (let r = 0; r < 11; r++) {
        sixLines.push({
            r,
            nums: [1, 2, 3, 4, 5, 6].map((i) => String(3 * r + i)),
        });
    }

    // Column positions (0/1/2 → percent-based). Each cell width = (100% - 8px) / 3.
    // Boundary between col c and c+1 sits at ((c+1)*(100%-8px)/3 + c*4 + 2px)
    const colBoundary = (c) => `calc(${((c + 1) * 100) / 3}% - ${8 / 3 * (c + 1)}px + ${c * GAP + GAP / 2}px)`;
    // Column *center* (for corner overlays at intersections)
    // Actually easier: for c ∈ {0,1}, corner sits at the right boundary of cell c
    const rowTop = (r) => `${r * (CELL_H + GAP)}px`;                       // top of row r
    const rowBoundary = (r) => `${(r + 1) * CELL_H + r * GAP + GAP / 2}px`; // horizontal line between row r and r+1

    // Amount getter for the chip stack
    const amountOn = (kind, nums) => {
        const k = keyOf(kind, nums, null);
        return bets.find((b) => b.key === k)?.amount || 0;
    };

    return (
        <div className="pointer-events-none absolute inset-0 z-20">
            {/* Horizontal splits (between col c and c+1) */}
            {hSplits.map(({ r, c, nums }) => (
                <button
                    key={`hs-${r}-${c}`}
                    onClick={() => onSplit(nums)}
                    className="absolute pointer-events-auto rounded-sm hover:bg-[#f0abfc]/25 transition flex items-center justify-center"
                    style={{
                        top: rowTop(r),
                        left: colBoundary(c),
                        height: `${CELL_H}px`,
                        width: `${HIT}px`,
                        transform: 'translateX(-50%)',
                    }}
                    title={`Split ${nums.join('/')} · 17:1`}
                    data-testid={`roulette-hsplit-${nums.join('-')}`}
                >
                    <LineChip amount={amountOn('split', nums)} />
                </button>
            ))}
            {/* Vertical splits (between row r and r+1 in col c) */}
            {vSplits.map(({ r, c, nums }) => (
                <button
                    key={`vs-${r}-${c}`}
                    onClick={() => onSplit(nums)}
                    className="absolute pointer-events-auto rounded-sm hover:bg-[#f0abfc]/25 transition flex items-center justify-center"
                    style={{
                        top: rowBoundary(r),
                        left: `calc(${(c * 100) / 3}% + ${c * GAP - 8 / 3 * c}px)`,
                        width: `calc(${100 / 3}% - ${8 / 3}px)`,
                        height: `${HIT}px`,
                        transform: 'translateY(-50%)',
                    }}
                    title={`Split ${nums.join('/')} · 17:1`}
                    data-testid={`roulette-vsplit-${nums.join('-')}`}
                >
                    <LineChip amount={amountOn('split', nums)} />
                </button>
            ))}
            {/* Corners (4-number intersections) */}
            {corners.map(({ r, c, nums }) => (
                <button
                    key={`cn-${r}-${c}`}
                    onClick={() => onCorner(nums)}
                    className="absolute pointer-events-auto rounded-full hover:bg-[#f0abfc]/35 transition flex items-center justify-center z-10"
                    style={{
                        top: rowBoundary(r),
                        left: colBoundary(c),
                        width: `${HIT + 4}px`,
                        height: `${HIT + 4}px`,
                        transform: 'translate(-50%, -50%)',
                    }}
                    title={`Corner ${nums.join('/')} · 8:1`}
                    data-testid={`roulette-corner-${nums.join('-')}`}
                >
                    <LineChip amount={amountOn('corner', nums)} />
                </button>
            ))}
            {/* Streets — small tabs on the RIGHT edge of each row */}
            {streets.map(({ r, nums }) => (
                <button
                    key={`st-${r}`}
                    onClick={() => onStreet(nums)}
                    className="absolute pointer-events-auto rounded-sm hover:bg-[#f0abfc]/25 transition flex items-center justify-center"
                    style={{
                        top: rowTop(r),
                        right: `-${HIT + 2}px`,
                        height: `${CELL_H}px`,
                        width: `${HIT}px`,
                    }}
                    title={`Street ${nums.join('/')} · 11:1`}
                    data-testid={`roulette-street-${nums.join('-')}`}
                >
                    <LineChip amount={amountOn('street', nums)} />
                </button>
            ))}
            {/* Six-lines — small tabs on the RIGHT edge at row boundaries */}
            {sixLines.map(({ r, nums }) => (
                <button
                    key={`sl-${r}`}
                    onClick={() => onSixLine(nums)}
                    className="absolute pointer-events-auto rounded-full hover:bg-[#f0abfc]/35 transition flex items-center justify-center z-10"
                    style={{
                        top: rowBoundary(r),
                        right: `-${HIT + 2}px`,
                        width: `${HIT + 4}px`,
                        height: `${HIT + 4}px`,
                        transform: 'translateY(-50%)',
                    }}
                    title={`Six-line ${nums[0]}–${nums[5]} · 5:1`}
                    data-testid={`roulette-sixline-${nums[0]}-${nums[5]}`}
                >
                    <LineChip amount={amountOn('six_line', nums)} />
                </button>
            ))}
        </div>
    );
};

const OutsideBtn = ({ label, amount, onClick, tone = 'purple', winner, testid }) => {
    const toneCls = {
        purple: 'bg-[#4a0a72]/70 text-[#f0abfc] border-[#c084fc]/70',
        red:    'text-white bg-[#c11b1b] border-[#c11b1b]',
        black:  'text-white bg-[#111] border-white/30',
    }[tone];
    return (
        <button
            onClick={onClick}
            className={`relative ${toneCls} border font-black uppercase tracking-wider text-[10px] md:text-xs h-11 md:h-12 flex items-center justify-center hover:brightness-125 transition rounded ${winner ? 'ring-4 ring-[#f0abfc] animate-pulse shadow-[0_0_18px_rgba(240,171,252,0.7)]' : ''}`}
            data-testid={testid}
        >
            {label}
            <BetStack amount={amount} />
        </button>
    );
};

export default function Roulette() {
    const nav = useNavigate();
    const { user, refreshUser } = useAuth();
    const audio = useCrapsAudio();
    const [muted, setMuted] = useState(false);
    const [winFlash, setWinFlash] = useState(null);   // current { amount, key, label }
    const [flashQueue, setFlashQueue] = useState([]); // pending premium-bet flashes
    // Belt-and-suspenders safety net — force-clears any lingering flash after
    // 7.5s so the overlay can NEVER get stuck on the screen. Re-armed on
    // every new spin.
    const flashSafetyRef = useRef(null);
    const clearFlashSafety = () => {
        if (flashSafetyRef.current) { clearTimeout(flashSafetyRef.current); flashSafetyRef.current = null; }
    };
    const armFlashSafety = () => {
        clearFlashSafety();
        flashSafetyRef.current = setTimeout(() => {
            setWinFlash(null); setFlashQueue([]); flashSafetyRef.current = null;
        }, 7500);
    };
    useEffect(() => () => clearFlashSafety(), []);
    const playFlashAudio = (amount) => {
        if (amount >= 500) audio.play('vault_kaching');
        else if (amount >= 100) audio.play('coin_cascade');
    };
    const handleFlashDone = () => {
        if (flashQueue.length > 0) {
            const [next, ...rest] = flashQueue;
            playFlashAudio(next.amount);
            setWinFlash(next);
            setFlashQueue(rest);
        } else {
            setWinFlash(null);
            clearFlashSafety();
        }
    };
    const round2r = (n) => Math.round(Number(n) * 100) / 100;

    // Casino-caller-style label per bet kind. Only "hero" premium bets
    // (straight up 35:1, split 17:1, streets, corners, top line, six line)
    // get individual pops. Outside 1:1 / 2:1 bets roll up into WINNER.
    const labelForRouletteBet = (bet) => {
        const nums = bet.numbers || [];
        switch (bet.kind) {
            case 'straight': return `POCKET ${nums[0] || ''}!`;
            case 'split':    return 'SPLIT!';
            case 'street':   return 'STREET!';
            case 'trio':     return 'TRIO!';
            case 'corner':   return 'CORNER!';
            case 'top_line': return 'TOP LINE!';
            case 'six_line': return 'SIX LINE!';
            default:         return null;   // outside bets → group into WINNER
        }
    };

    const buildRouletteFlashQueue = (resolved, winningPocket, roundId) => {
        const premium = [];
        let rollUp = 0;
        (resolved || []).forEach((r) => {
            if (r.result !== 'win') return;
            const net = round2r(Number(r.payout || 0) - Number(r.bet?.amount || 0));
            if (net <= 0) return;
            const label = labelForRouletteBet(r.bet || {});
            if (label) {
                premium.push({ amount: net, key: `rou-${r.bet.kind}-${winningPocket}-${roundId}-${Math.random()}`, label });
            } else {
                rollUp = round2r(rollUp + net);
            }
        });
        // Biggest premium hit first
        premium.sort((a, b) => b.amount - a.amount);
        if (rollUp > 0) {
            premium.push({ amount: rollUp, key: `rou-outside-${roundId}` });
        }
        return premium;
    };

    useEffect(() => { audio.setEffects(!muted); }, [muted, audio]);

    const [cfg, setCfg] = useState(null);
    const [chip, setChip] = useState(5);
    const [bets, setBets] = useState([]);
    const [lastBets, setLastBets] = useState(null);
    const [busy, setBusy] = useState(false);
    const [spinning, setSpinning] = useState(false);
    const [round, setRound] = useState(null);
    const [history, setHistory] = useState([]);
    const [recentRounds, setRecentRounds] = useState([]);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [rulesOpen, setRulesOpen] = useState(false);

    // Bet-mode builder — when mode !== 'straight', tapping a number cell adds
    // it to `building`; once the selection matches a valid combo for the mode,
    // the chip is auto-placed. Cancel with the ✕ badge below the felt.
    const [mode, setMode] = useState('straight');
    const [building, setBuilding] = useState([]);   // list of number strings being combined

    // Saved bet layouts (favorites) — persisted to localStorage keyed by user.
    // Each favorite: { id, name, bets: [{kind, numbers, selector, amount}, ...], created_at }
    const favKey = user?.id ? `roulette_favorites_${user.id}` : null;
    const [favorites, setFavorites] = useState([]);
    const [favName, setFavName] = useState('');
    useEffect(() => {
        if (!favKey) return;
        try {
            const raw = window.localStorage.getItem(favKey);
            setFavorites(raw ? JSON.parse(raw) : []);
        } catch { setFavorites([]); }
    }, [favKey]);
    const persistFavs = (next) => {
        setFavorites(next);
        if (favKey) {
            try { window.localStorage.setItem(favKey, JSON.stringify(next)); } catch { /* ignore quota */ }
        }
    };
    const saveCurrentAsFav = () => {
        if (bets.length === 0) { toast.error('Place some bets before saving'); return; }
        const name = (favName || '').trim() || `Layout ${favorites.length + 1}`;
        if (favorites.some((f) => f.name.toLowerCase() === name.toLowerCase())) {
            toast.error(`"${name}" already exists`); return;
        }
        const snap = {
            id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
            name,
            bets: bets.map((b) => ({ kind: b.kind, numbers: b.numbers, selector: b.selector, amount: b.amount })),
            created_at: new Date().toISOString(),
        };
        persistFavs([snap, ...favorites].slice(0, 20));  // cap at 20 layouts
        setFavName('');
        toast.success(`Saved "${name}"`);
    };
    const loadFav = (fav) => {
        const total = (fav.bets || []).reduce((s, b) => s + Number(b.amount || 0), 0);
        if (cfg?.max_total_wager && total > cfg.max_total_wager + 0.001) {
            toast.error(`"${fav.name}" exceeds table max $${cfg.max_total_wager}`); return;
        }
        if (balance + 0.001 < total) { toast.error(`Not enough balance to load "${fav.name}"`); return; }
        audio.play('chip');
        setBets(fav.bets.map((b) => ({
            key: keyOf(b.kind, b.numbers, b.selector),
            kind: b.kind, numbers: b.numbers, selector: b.selector, amount: b.amount,
        })));
        toast.success(`Loaded "${fav.name}"`);
    };
    const deleteFav = (id) => {
        persistFavs(favorites.filter((f) => f.id !== id));
    };

    const balance = Number(user?.credits || 0) + Number(user?.bonus_credits || 0);
    const totalWager = useMemo(() => bets.reduce((s, b) => s + Number(b.amount || 0), 0), [bets]);

    useEffect(() => {
        (async () => {
            try {
                const { data: c } = await axios.get(`${API}/roulette/config`, { withCredentials: true });
                setCfg(c);
                if (c?.chip_denominations?.length) setChip(c.chip_denominations[1] || c.chip_denominations[0]);
                const { data: rs } = await axios.get(`${API}/roulette/recent-spins?limit=${c.history_length}`, { withCredentials: true });
                setHistory(rs.spins || []);
                const { data: mine } = await axios.get(`${API}/roulette/history/mine?limit=20`, { withCredentials: true });
                setRecentRounds(mine || []);
            } catch (e) {
                toast.error(e.response?.data?.detail || 'Failed to load roulette');
            }
        })();
    }, []);

    const keyOf = useCallback((kind, numbers, selector) =>
        `${kind}|${(numbers || []).slice().sort().join(',')}|${selector ?? ''}`,
    []);

    // ── Combo validators (must match backend) ──────────────────────────
    // Sort numbers to compare against pre-computed valid sets.
    const eqSet = (a, b) => a.length === b.length && a.every((x) => b.includes(x));

    const isValidSplit = (nums) => {
        if (nums.length !== 2) return false;
        // 0/00 special splits
        const s = new Set(nums);
        if ((s.has('0') && s.has('1')) || (s.has('0') && s.has('2')) ||
            (s.has('00') && s.has('2')) || (s.has('00') && s.has('3')) ||
            (s.has('0') && s.has('00'))) return true;
        if (nums.some((n) => n === '0' || n === '00')) return false;
        const [a, b] = nums.map(Number).sort((x, y) => x - y);
        // Horizontal (a, a+1) where a%3 != 0
        if (b === a + 1 && a % 3 !== 0) return true;
        // Vertical (a, a+3)
        if (b === a + 3) return true;
        return false;
    };
    const isValidStreet = (nums) => {
        if (nums.length !== 3 || nums.some((n) => n === '0' || n === '00')) return false;
        const sorted = nums.map(Number).sort((x, y) => x - y);
        return sorted[1] === sorted[0] + 1 && sorted[2] === sorted[0] + 2 && sorted[0] % 3 === 1;
    };
    const isValidCorner = (nums) => {
        if (nums.length !== 4 || nums.some((n) => n === '0' || n === '00')) return false;
        const sorted = nums.map(Number).sort((x, y) => x - y);
        const [a, b, c, d] = sorted;
        return b === a + 1 && c === a + 3 && d === a + 4 && a % 3 !== 0;
    };
    const isValidSixLine = (nums) => {
        if (nums.length !== 6 || nums.some((n) => n === '0' || n === '00')) return false;
        const sorted = nums.map(Number).sort((x, y) => x - y);
        return sorted.every((v, i) => v === sorted[0] + i) && sorted[0] % 3 === 1 && sorted[5] <= 36;
    };
    const isValidTrio = (nums) => {
        const s = new Set(nums);
        if (nums.length !== 3) return false;
        return (s.has('0') && s.has('1') && s.has('2')) ||
               (s.has('0') && s.has('00') && s.has('2')) ||
               (s.has('00') && s.has('2') && s.has('3'));
    };
    const isValidTopLine = (nums) => eqSet(nums, ['0', '00', '1', '2', '3']);

    const modeConfig = {
        straight: { size: 1, validate: () => true, kind: 'straight' },
        split:    { size: 2, validate: isValidSplit, kind: 'split' },
        street:   { size: 3, validate: isValidStreet, kind: 'street' },
        trio:     { size: 3, validate: isValidTrio, kind: 'trio' },
        corner:   { size: 4, validate: isValidCorner, kind: 'corner' },
        top_line: { size: 5, validate: isValidTopLine, kind: 'top_line' },
        six_line: { size: 6, validate: isValidSixLine, kind: 'six_line' },
    };

    const placeBet = useCallback((kind, numbers, selector = null) => {
        if (spinning || busy) return;
        const cost = Number(chip);
        const insideKinds = ['straight','split','street','trio','corner','top_line','six_line'];
        const isInside = insideKinds.includes(kind);
        const cap = isInside ? cfg?.max_inside_bet : cfg?.max_outside_bet;
        const k = keyOf(kind, numbers, selector);
        const existing = bets.find(b => b.key === k);
        const nextAmt = (existing?.amount || 0) + cost;
        if (cap && nextAmt > cap + 0.001) { toast.error(`${isInside ? 'Inside' : 'Outside'} bet capped at $${cap}`); return; }
        if (cfg?.max_total_wager && totalWager + cost > cfg.max_total_wager + 0.001) { toast.error(`Table max $${cfg.max_total_wager} per spin`); return; }
        if (balance + 0.001 < totalWager + cost) { toast.error('Insufficient balance'); return; }
        audio.play('chip');
        setBets(prev => existing
            ? prev.map(b => b.key === k ? { ...b, amount: nextAmt } : b)
            : [...prev, { key: k, kind, numbers, selector, amount: cost }]
        );
    }, [chip, cfg, bets, balance, totalWager, keyOf, audio, spinning, busy]);

    // Handle a click on a felt number cell. In straight mode, places a chip
    // instantly. In any combo mode, adds the number to the building set and
    // auto-places if the combo is now valid and fully sized.
    const tapNumber = useCallback((n) => {
        if (spinning || busy) return;
        if (mode === 'straight') { placeBet('straight', [n]); return; }
        const conf = modeConfig[mode];
        if (!conf) return;
        // Reject 0/00 in modes that don't allow them (except split/trio/top_line)
        if ((n === '0' || n === '00') && !['split', 'trio', 'top_line'].includes(mode)) {
            toast.error(`${mode.replace('_', ' ')} does not include ${n}`); return;
        }
        if (building.includes(n)) {
            // Tap same number → remove from selection
            setBuilding((prev) => prev.filter((x) => x !== n));
            return;
        }
        const next = [...building, n];
        if (next.length > conf.size) { toast.error(`${mode.replace('_', ' ')} takes ${conf.size} numbers`); return; }
        if (next.length === conf.size) {
            if (conf.validate(next)) {
                placeBet(conf.kind, next);
                setBuilding([]);
            } else {
                toast.error(`Not a valid ${mode.replace('_', ' ')}`);
                setBuilding([]);
            }
        } else {
            setBuilding(next);
        }
    }, [mode, building, placeBet, spinning, busy]);

    const amountOn = useCallback((kind, numbers, selector = null) => {
        const k = keyOf(kind, numbers, selector);
        return bets.find(b => b.key === k)?.amount || 0;
    }, [bets, keyOf]);

    const undoLast = () => { if (!bets.length) return; audio.play('chip'); setBets(p => p.slice(0, -1)); };
    const clearAll = () => { if (!bets.length) return; audio.play('chip'); setBets([]); };
    const doubleAll = () => {
        if (!bets.length) return;
        const newTotal = totalWager * 2;
        if (cfg?.max_total_wager && newTotal > cfg.max_total_wager + 0.001) { toast.error(`Exceeds $${cfg.max_total_wager} table max`); return; }
        if (balance + 0.001 < newTotal) { toast.error('Insufficient balance'); return; }
        audio.play('chip'); setBets(p => p.map(b => ({ ...b, amount: b.amount * 2 })));
    };
    const rebet = () => {
        if (!lastBets || !lastBets.length) return;
        const total = lastBets.reduce((s, b) => s + Number(b.amount || 0), 0);
        if (balance + 0.001 < total) { toast.error('Not enough balance for rebet'); return; }
        audio.play('chip'); setBets(lastBets.map(b => ({ ...b })));
    };

    const spin = async () => {
        if (busy || spinning) return;
        if (bets.length === 0) { toast.error('Place a bet first'); return; }
        setBusy(true); setRound(null);
        const idempotency_key = `rou-${user?.id}-${Date.now()}`;
        try {
            const { data } = await axios.post(`${API}/roulette/spin`, {
                bets: bets.map(b => ({ kind: b.kind, numbers: b.numbers, selector: b.selector, amount: b.amount })),
                idempotency_key,
            }, { withCredentials: true });
            setRound(data);
            setLastBets(bets.map(b => ({ ...b })));
            setSpinning(true);
            audio.play('roll');
            audio.play('wheel_whoosh');
        } catch (e) {
            const detail = e.response?.data?.detail;
            const msg = typeof detail === 'string' ? detail :
                Array.isArray(detail) ? detail.map(x => x?.msg || JSON.stringify(x)).join('; ') :
                'Spin failed';
            toast.error(msg);
            setBusy(false);
        }
    };

    const onSpinDone = () => {
        setSpinning(false); setBusy(false); audio.play('roll_end');
        if (round) {
            // Log the settle payload so we can debug "I won nothing"
            // reports (Iter 285) — check the browser console if a win
            // ever seems missing.
            try { console.log('[Roulette] settle:', round); } catch { /* noop */ }
            if (Number(round.net) > 0) {
                audio.play(Number(round.net) >= 100 ? 'win_big' : 'win_small');
                // Big, sticky, 6-second win toast so it can't be missed.
                toast.success(
                    `🎉  ${round.winning_pocket} ${round.winning_color.toUpperCase()}  —  Won +$${round.net.toFixed(2)}`,
                    {
                        duration: 6000,
                        description: `Paid $${Number(round.total_payout).toFixed(2)} · Wagered $${Number(round.total_wager).toFixed(2)}`,
                    },
                );
                // Break out premium inside-bet wins so a straight-up hit gets
                // its own labelled "POCKET N!" pop. Outside 1:1 / 2:1 wins roll
                // up into a single WINNER flash to keep the queue short.
                const q = buildRouletteFlashQueue(round.resolved || [], round.winning_pocket, round.id);
                if (q.length > 0) {
                    const bounded = q.slice(0, 3);
                    playFlashAudio(bounded[0].amount);
                    setWinFlash(bounded[0]);
                    setFlashQueue(bounded.slice(1));
                    armFlashSafety();
                }
            } else if (Number(round.total_payout) > 0) {
                // Wager returned in full (e.g., a push scenario) or partial.
                audio.play('lose');
                toast.message(
                    `${round.winning_pocket} ${round.winning_color.toUpperCase()}`,
                    { description: `Paid $${Number(round.total_payout).toFixed(2)} · Wagered $${Number(round.total_wager).toFixed(2)}`, duration: 4000 },
                );
            } else {
                audio.play('lose');
                toast.message(`${round.winning_pocket} ${round.winning_color.toUpperCase()}`, { duration: 4000 });
            }
            setHistory(prev => [{ pocket: round.winning_pocket, color: round.winning_color, at: round.created_at }, ...prev].slice(0, cfg?.history_length || 20));
            setRecentRounds(prev => [{
                id: round.id, pocket: round.winning_pocket, color: round.winning_color,
                wagered: round.total_wager, paid: round.total_payout, net: round.net,
                at: round.created_at, bet_count: round.bets?.length || 0,
            }, ...prev].slice(0, 20));
            setBets([]);
            refreshUser?.();
        }
    };

    const winnerPocket = (!spinning && round) ? round.winning_pocket : null;
    const winnerNum = (winnerPocket && winnerPocket !== '0' && winnerPocket !== '00') ? parseInt(winnerPocket, 10) : null;

    if (!cfg) {
        return <div className="min-h-screen bg-black text-white flex items-center justify-center">Loading roulette…</div>;
    }
    if (!cfg.enabled) {
        return (
            <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center p-6 text-center">
                <div className="text-2xl font-black text-[#c084fc] mb-2">Roulette table closed</div>
                <div className="text-white/70">Check back soon.</div>
                <button onClick={() => nav('/lobby')} className="mt-4 px-4 py-2 rounded-full bg-[#c084fc] text-black font-black">Back to lobby</button>
            </div>
        );
    }

    return (
        <div className="min-h-screen text-white pb-24"
             style={{ background: 'radial-gradient(ellipse at 30% 20%, rgba(107, 33, 168, 0.7) 0%, #24054a 40%, #0a0118 100%)' }}
             data-testid="roulette-page">
            <WinFlash win={winFlash} label={winFlash?.label || 'WINNER'} onDone={handleFlashDone} duration={1400} />
            <header className="sticky top-0 z-30 bg-black/70 backdrop-blur border-b border-[#c084fc]/40">
                <div className="max-w-6xl mx-auto flex items-center justify-between px-3 py-2 gap-3">
                    <button onClick={() => nav('/lobby')} className="flex items-center gap-1.5 text-[#f0abfc] hover:text-white shrink-0" data-testid="roulette-back-btn">
                        <ArrowLeft className="w-4 h-4" /><span className="text-sm hidden sm:inline">Casino Floor</span>
                    </button>
                    <h1 className="text-base sm:text-xl font-black tracking-wider">
                        <span className="text-[#c084fc]" style={{ textShadow: '0 0 8px rgba(192,132,252,0.7)' }}>BETO</span>
                        <span className="text-white">·ROULETTE</span>
                    </h1>
                    <div className="flex items-center gap-1.5">
                        <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/60 border border-[#c084fc]/50" data-testid="roulette-wallet-chip">
                            <Coins className="w-3.5 h-3.5 text-[#c084fc]" />
                            <span className="font-mono text-sm font-black text-[#f0abfc]">${balance.toFixed(2)}</span>
                        </div>
                        <button onClick={() => setHistoryOpen(true)} className="p-1.5 rounded-md hover:bg-white/5 text-[#f0abfc]/80" title="My history" data-testid="roulette-history-btn"><History className="w-4 h-4" /></button>
                        <button onClick={() => setMuted(m => !m)} className="p-1.5 rounded-md hover:bg-white/5" title={muted ? 'Unmute' : 'Mute'} data-testid="roulette-mute-btn">
                            {muted ? <VolumeX className="w-4 h-4 text-[#FF3B30]" /> : <Volume2 className="w-4 h-4 text-[#39FF14]" />}
                        </button>
                        <button onClick={() => audio.setMusic(!audio.musicOn)} className="p-1.5 rounded-md hover:bg-white/5" title={audio.musicOn ? 'Music off' : 'Music on'} data-testid="roulette-music-btn">
                            <Music className={`w-4 h-4 ${audio.musicOn ? 'text-[#00E1FF]' : 'text-white/40'}`} />
                        </button>
                        <button onClick={() => setRulesOpen(true)} className="p-1.5 rounded-md hover:bg-white/5" title="Rules" data-testid="roulette-rules-btn"><HelpCircle className="w-4 h-4 text-[#c084fc]" /></button>
                    </div>
                </div>
            </header>

            <div className="max-w-6xl mx-auto px-2 md:px-4 pt-3">
                <div className="grid lg:grid-cols-[380px_1fr] gap-4">
                    <div>
                        <RouletteWheel wheelOrder={cfg.wheel_order} resultPocket={round?.winning_pocket} spinning={spinning} onSpinDone={onSpinDone} onClack={() => audio.play('clack')} />
                        <div className="mt-3">
                            <p className="text-[10px] uppercase tracking-widest text-[#c084fc] font-black mb-1">Recent numbers</p>
                            <div className="flex flex-wrap gap-1" data-testid="roulette-recent">
                                {(history || []).slice(0, cfg.history_length).map((h, i) => {
                                    const c = h.color;
                                    const bg = c === 'green' ? 'bg-[#7c3aed]' : (c === 'red' ? 'bg-[#c11b1b]' : 'bg-[#111]');
                                    return (
                                        <div key={i} className={`${bg} border border-[#c084fc]/50 w-8 h-8 rounded flex items-center justify-center text-xs font-black text-white`}>
                                            {h.pocket}
                                        </div>
                                    );
                                })}
                                {history.length === 0 && <div className="text-xs text-white/50 italic">No spins yet</div>}
                            </div>
                        </div>

                        {round && !spinning && (
                            <div className="mt-3 rounded-xl border border-[#c084fc]/50 bg-black/50 p-3 shadow-[0_0_16px_rgba(168,85,247,0.2)]" data-testid="roulette-result-panel">
                                <div className="flex items-center gap-3">
                                    <div className={`w-14 h-14 rounded-full flex items-center justify-center font-black text-2xl text-white border-2 border-[#c084fc] ${round.winning_color === 'green' ? 'bg-[#7c3aed]' : (round.winning_color === 'red' ? 'bg-[#c11b1b]' : 'bg-[#111]')}`}>
                                        {round.winning_pocket}
                                    </div>
                                    <div className="flex-1">
                                        <div className="text-xs uppercase tracking-widest text-white/60">Result</div>
                                        <div className="font-black text-lg">{round.winning_color.toUpperCase()} {round.winning_pocket}</div>
                                        <div className="text-xs font-mono">
                                            Wagered <b className="text-white">${round.total_wager.toFixed(2)}</b> ·
                                            Paid <b className="text-[#f0abfc]">${round.total_payout.toFixed(2)}</b> ·
                                            Net <b className={round.net >= 0 ? 'text-[#39FF14]' : 'text-[#FF3B30]'}>{round.net >= 0 ? '+' : ''}${round.net.toFixed(2)}</b>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div>
                        {/* Combo bet mode selector */}
                        <div className="mb-2 rounded-xl border border-[#c084fc]/40 bg-black/50 p-2 shadow-[0_0_18px_rgba(168,85,247,0.15)]" data-testid="roulette-mode-selector">
                            <div className="flex items-center gap-2 mb-1.5">
                                <span className="text-[10px] uppercase tracking-widest text-[#c084fc] font-black">Bet mode</span>
                                {mode !== 'straight' && building.length > 0 && (
                                    <span className="text-[10px] text-white/80 font-mono">
                                        Pick <b className="text-[#f0abfc]">{modeConfig[mode].size - building.length}</b> more
                                    </span>
                                )}
                                {mode !== 'straight' && building.length > 0 && (
                                    <button
                                        onClick={() => setBuilding([])}
                                        className="ml-auto text-[10px] text-[#FF3B30] hover:underline"
                                        data-testid="roulette-mode-cancel"
                                    >
                                        Cancel selection
                                    </button>
                                )}
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {[
                                    { k: 'straight', label: 'Straight', hint: '35:1' },
                                    { k: 'split',    label: 'Split',    hint: '17:1' },
                                    { k: 'street',   label: 'Street',   hint: '11:1' },
                                    { k: 'trio',     label: 'Trio',     hint: '11:1' },
                                    { k: 'corner',   label: 'Corner',   hint: '8:1' },
                                    { k: 'six_line', label: 'Six-Line', hint: '5:1' },
                                    { k: 'top_line', label: 'Top Line', hint: '6:1' },
                                ].map((m) => (
                                    <button
                                        key={m.k}
                                        onClick={() => { setMode(m.k); setBuilding([]); }}
                                        className={`px-3 py-1.5 rounded text-[11px] font-black uppercase tracking-wider border transition ${
                                            mode === m.k
                                                ? 'bg-[#c084fc] text-black border-[#c084fc] shadow-[0_0_12px_rgba(192,132,252,0.6)]'
                                                : 'bg-black/40 text-white/80 border-[#c084fc]/25 hover:border-[#c084fc]/70'
                                        }`}
                                        data-testid={`roulette-mode-${m.k}`}
                                    >
                                        {m.label}
                                        <span className={`ml-1 font-mono ${mode === m.k ? 'text-black/60' : 'text-[#f0abfc]/80'}`}>{m.hint}</span>
                                    </button>
                                ))}
                            </div>
                            {mode !== 'straight' && (
                                <div className="mt-1.5 text-[10px] text-white/60 italic">
                                    Tap numbers to build your <b>{mode.replace('_', ' ')}</b> · {modeConfig[mode].size} needed
                                    {building.length > 0 && ` · Building: ${building.join(', ')}`}
                                </div>
                            )}
                        </div>
                        <div className="rounded-xl overflow-hidden border-2 border-[#c084fc]/60 shadow-[0_0_28px_rgba(168,85,247,0.25)]" style={{ background: 'radial-gradient(ellipse at 50% 30%, #4a0a72 0%, #24054a 60%, #0a0118 100%)' }}>
                            {/* ─── FELT-ENGRAVED PAYOUTS + HOUSE RULES ───────
                                Silk-screened onto the top of the felt like the
                                arced payout ribbon on a real roulette table. */}
                            <div className="pt-2 md:pt-3 pb-1.5 md:pb-2 px-2 md:px-3 border-b border-[#c084fc]/25 text-center pointer-events-none flex flex-col items-center" data-testid="roulette-felt-rules">
                                <HouseCrest size={40} accent="#c084fc" glow="rgba(192,132,252,0.6)" />
                                <div className="font-casino text-[10px] md:text-[13px] tracking-[0.35em] md:tracking-[0.5em] mt-1" style={{ color: '#f0abfc', textShadow: '0 0 10px rgba(240,171,252,0.65), 0 0 18px rgba(168,85,247,0.4), 0 1px 0 rgba(0,0,0,0.6)' }}>
                                    BETOHALICS · AMERICAN ROULETTE
                                </div>
                                <div className="font-casino text-[7px] md:text-[9px] mt-0.5 md:mt-1 tracking-[0.14em] leading-tight" style={{ color: '#f0abfc', textShadow: '0 0 6px rgba(240,171,252,0.55), 0 1px 0 rgba(0,0,0,0.6)' }}>
                                    STRAIGHT 35:1 · SPLIT 17:1 · STREET 11:1 · CORNER 8:1 · LINE 5:1 · COL / DOZEN 2:1 · EVEN 1:1
                                </div>
                                <div className="font-casino text-[7px] md:text-[9px] tracking-[0.14em] opacity-85 leading-tight" style={{ color: '#f0abfc', textShadow: '0 0 6px rgba(240,171,252,0.5), 0 1px 0 rgba(0,0,0,0.6)' }}>
                                    MAX INSIDE ${cfg.max_inside_bet ?? 25} · MAX OUTSIDE ${cfg.max_outside_bet ?? 100} · MAX PAYOUT ${(cfg.max_payout ?? 3500).toLocaleString()}
                                </div>
                            </div>
                            <div className="p-2 md:p-3 grid grid-cols-[36px_1fr_60px] gap-1" data-testid="roulette-felt">
                                <div className="grid grid-rows-2 gap-1">
                                    <button
                                        onClick={() => tapNumber('0')}
                                        className={`relative bg-[#7c3aed] text-white font-black border border-[#c084fc]/70 hover:brightness-125 flex items-center justify-center text-sm rounded transition ${building.includes('0') ? 'ring-2 ring-[#f0abfc]' : ''} ${winnerPocket === '0' ? 'ring-4 ring-[#f0abfc] animate-pulse shadow-[0_0_18px_rgba(240,171,252,0.7)]' : ''}`}
                                        style={{ height: `${6 * CELL_H + 5 * GAP}px` }}
                                        data-testid="roulette-num-0"
                                    >0<BetStack amount={amountOn('straight', ['0'])} /></button>
                                    <button
                                        onClick={() => tapNumber('00')}
                                        className={`relative bg-[#7c3aed] text-white font-black border border-[#c084fc]/70 hover:brightness-125 flex items-center justify-center text-sm rounded transition ${building.includes('00') ? 'ring-2 ring-[#f0abfc]' : ''} ${winnerPocket === '00' ? 'ring-4 ring-[#f0abfc] animate-pulse shadow-[0_0_18px_rgba(240,171,252,0.7)]' : ''}`}
                                        style={{ height: `${6 * CELL_H + 5 * GAP}px` }}
                                        data-testid="roulette-num-00"
                                    >00<BetStack amount={amountOn('straight', ['00'])} /></button>
                                </div>
                                <div className="relative">
                                    <div className="grid grid-cols-3 gap-1" data-testid="roulette-grid">
                                        {GRID_ROWS.map((row) => row.map((n) => (
                                            <NumberCell
                                                key={n} n={n}
                                                amount={amountOn('straight', [n])}
                                                onClick={() => tapNumber(n)}
                                                winner={winnerPocket === n}
                                                highlight={building.includes(n)}
                                            />
                                        )))}
                                    </div>
                                    <LineOverlays
                                        bets={bets}
                                        onSplit={(nums) => placeBet('split', nums)}
                                        onCorner={(nums) => placeBet('corner', nums)}
                                        onStreet={(nums) => placeBet('street', nums)}
                                        onSixLine={(nums) => placeBet('six_line', nums)}
                                        keyOf={keyOf}
                                    />
                                </div>
                                <div className="grid grid-rows-3 gap-1">
                                    {[1, 2, 3].map((c) => (
                                        <OutsideBtn
                                            key={c}
                                            label="2:1"
                                            amount={amountOn('column', null, c)}
                                            onClick={() => placeBet('column', null, c)}
                                            testid={`roulette-col-${c}`}
                                            winner={winnerNum !== null && (winnerNum % 3 === (c === 3 ? 0 : c))}
                                        />
                                    ))}
                                </div>

                                <div />
                                <div className="grid grid-cols-3 gap-1 mt-1" data-testid="roulette-dozens">
                                    {[1, 2, 3].map((d) => (
                                        <OutsideBtn
                                            key={d}
                                            label={d === 1 ? '1st 12' : d === 2 ? '2nd 12' : '3rd 12'}
                                            amount={amountOn('dozen', null, d)}
                                            onClick={() => placeBet('dozen', null, d)}
                                            testid={`roulette-dozen-${d}`}
                                            winner={winnerNum !== null && ((d === 1 && winnerNum <= 12) || (d === 2 && winnerNum >= 13 && winnerNum <= 24) || (d === 3 && winnerNum >= 25))}
                                        />
                                    ))}
                                </div>
                                <div />

                                <div />
                                <div className="grid grid-cols-6 gap-1 mt-1" data-testid="roulette-outside">
                                    <OutsideBtn label="1-18"  amount={amountOn('low')}   onClick={() => placeBet('low')}   testid="roulette-low"  winner={winnerNum !== null && winnerNum <= 18} />
                                    <OutsideBtn label="EVEN"  amount={amountOn('even')}  onClick={() => placeBet('even')}  testid="roulette-even" winner={winnerNum !== null && winnerNum % 2 === 0} />
                                    <OutsideBtn label="RED"   amount={amountOn('red')}   onClick={() => placeBet('red')}   testid="roulette-red"  tone="red"   winner={round?.winning_color === 'red'} />
                                    <OutsideBtn label="BLACK" amount={amountOn('black')} onClick={() => placeBet('black')} testid="roulette-black" tone="black" winner={round?.winning_color === 'black'} />
                                    <OutsideBtn label="ODD"   amount={amountOn('odd')}   onClick={() => placeBet('odd')}   testid="roulette-odd"  winner={winnerNum !== null && winnerNum % 2 === 1} />
                                    <OutsideBtn label="19-36" amount={amountOn('high')}  onClick={() => placeBet('high')}  testid="roulette-high" winner={winnerNum !== null && winnerNum >= 19} />
                                </div>
                                <div />
                            </div>
                        </div>

                        <div className="mt-3 rounded-xl border border-[#c084fc]/40 bg-black/50 p-3 shadow-[0_0_16px_rgba(168,85,247,0.15)]">
                            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                                <div className="text-[10px] uppercase tracking-widest text-[#c084fc] font-black">Select chip</div>
                                <div className="text-xs font-mono">
                                    Wager <b className="text-white" data-testid="roulette-total-wager">${totalWager.toFixed(2)}</b> ·
                                    Positions <b className="text-white">{bets.length}</b> ·
                                    Bal <b className="text-[#f0abfc]">${balance.toFixed(2)}</b>
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-2 items-center" data-testid="roulette-chip-selector">
                                {cfg.chip_denominations.map((v) => (
                                    <button key={v} onClick={() => setChip(v)} className={`transition ${chip === v ? 'ring-2 ring-[#f0abfc] scale-110 shadow-[0_0_12px_rgba(240,171,252,0.7)]' : 'opacity-80 hover:opacity-100'}`} data-testid={`roulette-chip-${v}`}>
                                        <Chip value={v} size={40} />
                                    </button>
                                ))}
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2 justify-center" data-testid="roulette-actions">
                                <button onClick={undoLast} disabled={!bets.length || busy || spinning} className="px-3 py-2 rounded-lg bg-black/60 border border-white/20 text-white text-xs font-black flex items-center gap-1 hover:border-[#c084fc] disabled:opacity-40" data-testid="roulette-undo-btn"><Undo2 className="w-3.5 h-3.5" />Undo</button>
                                <button onClick={clearAll} disabled={!bets.length || busy || spinning} className="px-3 py-2 rounded-lg bg-black/60 border border-white/20 text-[#FF3B30] text-xs font-black flex items-center gap-1 hover:border-[#FF3B30] disabled:opacity-40" data-testid="roulette-clear-btn"><RotateCcw className="w-3.5 h-3.5" />Clear</button>
                                <button onClick={doubleAll} disabled={!bets.length || busy || spinning} className="px-3 py-2 rounded-lg bg-black/60 border border-[#00E1FF]/50 text-[#00E1FF] text-xs font-black hover:border-[#00E1FF] disabled:opacity-40" data-testid="roulette-double-btn">2× DOUBLE</button>
                                <button onClick={rebet} disabled={!lastBets || busy || spinning} className="px-3 py-2 rounded-lg bg-black/60 border border-[#c084fc]/50 text-[#c084fc] text-xs font-black flex items-center gap-1 hover:border-[#c084fc] disabled:opacity-40" data-testid="roulette-rebet-btn"><Repeat className="w-3.5 h-3.5" />Rebet</button>
                                <button
                                    onClick={spin}
                                    disabled={busy || spinning || bets.length === 0}
                                    className="px-7 py-2 rounded-lg font-black text-base tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
                                    style={{ background: 'linear-gradient(135deg, #c084fc 0%, #7c3aed 60%, #6b21a8 100%)', color: '#fff', boxShadow: '0 0 22px rgba(192,132,252,0.55), inset 0 1px 0 rgba(255,255,255,0.25)' }}
                                    data-testid="roulette-spin-btn"
                                >
                                    {spinning ? 'SPINNING…' : busy ? 'PROCESSING…' : 'SPIN'}
                                </button>
                            </div>
                        </div>

                        {/* Favorites — saved bet layouts */}
                        {cfg.favorites_enabled !== false && (
                            <div className="mt-3 rounded-xl border border-[#c084fc]/30 bg-black/40 p-3" data-testid="roulette-favorites">
                                <div className="flex items-center gap-2 mb-2">
                                    <Star className="w-4 h-4 text-[#c084fc]" />
                                    <div className="text-[10px] uppercase tracking-widest text-[#c084fc] font-black">My Layouts</div>
                                    <span className="text-[10px] text-white/50">Save & one-tap load your bet spreads</span>
                                </div>
                                <div className="flex gap-2 items-center">
                                    <input
                                        value={favName}
                                        onChange={(e) => setFavName(e.target.value)}
                                        placeholder="Name this layout…"
                                        maxLength={32}
                                        className="flex-1 min-w-0 px-3 py-2 text-xs rounded-lg bg-black/60 border border-white/15 text-white placeholder:text-white/40 focus:outline-none focus:border-[#c084fc]"
                                        data-testid="roulette-fav-name"
                                    />
                                    <button
                                        onClick={saveCurrentAsFav}
                                        disabled={!bets.length}
                                        className="px-3 py-2 rounded-lg bg-[#c084fc] hover:bg-[#a855f7] text-black text-xs font-black flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                                        data-testid="roulette-fav-save"
                                    >
                                        <Save className="w-3.5 h-3.5" />Save
                                    </button>
                                </div>
                                {favorites.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid="roulette-fav-list">
                                        {favorites.map((f) => {
                                            const total = f.bets.reduce((s, b) => s + Number(b.amount || 0), 0);
                                            return (
                                                <div key={f.id} className="flex items-center rounded-lg border border-[#c084fc]/40 bg-black/50 overflow-hidden" data-testid={`roulette-fav-${f.id}`}>
                                                    <button
                                                        onClick={() => loadFav(f)}
                                                        disabled={busy || spinning}
                                                        className="px-2.5 py-1.5 text-xs font-black text-white hover:bg-[#c084fc]/20 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                                                        title={`Load: ${f.bets.length} bet(s) · $${total.toFixed(2)} total`}
                                                    >
                                                        {f.name}
                                                        <span className="text-[9px] font-mono text-[#f0abfc]">${total.toFixed(0)}</span>
                                                    </button>
                                                    <button
                                                        onClick={() => deleteFav(f.id)}
                                                        className="px-1.5 py-1.5 text-white/50 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10"
                                                        title={`Delete "${f.name}"`}
                                                        data-testid={`roulette-fav-delete-${f.id}`}
                                                    >
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                {favorites.length === 0 && (
                                    <p className="mt-2 text-[10px] text-white/40 italic">No saved layouts yet — place a spread and hit Save.</p>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <AnimatePresence>
                {historyOpen && (
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-3"
                        onClick={() => setHistoryOpen(false)}
                    >
                        <motion.div
                            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
                            className="bg-[#120404] border border-[#FFD700]/40 rounded-2xl w-full max-w-md p-4 max-h-[80vh] overflow-y-auto"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3 className="text-lg font-black text-[#FFD700] mb-2">My recent roulette rounds</h3>
                            {recentRounds.length === 0 ? (
                                <div className="text-white/60 text-sm italic">No spins yet.</div>
                            ) : (
                                <div className="space-y-1.5">
                                    {recentRounds.map((r) => (
                                        <div key={r.id} className="flex items-center justify-between rounded border border-white/10 bg-black/40 p-2 text-xs">
                                            <div className={`w-8 h-8 rounded flex items-center justify-center font-black text-white ${r.color === 'green' ? 'bg-[#0a8a3a]' : (r.color === 'red' ? 'bg-[#c11b1b]' : 'bg-[#111]')}`}>{r.pocket}</div>
                                            <div className="flex-1 mx-2 font-mono">
                                                <div>${r.wagered.toFixed(2)} → ${r.paid.toFixed(2)} · {r.bet_count} bet(s)</div>
                                                <div className="text-white/50">{new Date(r.at).toLocaleString()}</div>
                                            </div>
                                            <div className={`font-black ${r.net >= 0 ? 'text-[#39FF14]' : 'text-[#FF3B30]'}`}>
                                                {r.net >= 0 ? '+' : ''}${r.net.toFixed(2)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <button onClick={() => setHistoryOpen(false)} className="mt-3 w-full py-2 rounded-lg bg-[#FFD700] text-black font-black">Close</button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {rulesOpen && (
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-3"
                        onClick={() => setRulesOpen(false)}
                    >
                        <motion.div
                            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
                            className="bg-[#120404] border border-[#FFD700]/40 rounded-2xl w-full max-w-md p-4 max-h-[80vh] overflow-y-auto"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3 className="text-lg font-black text-[#FFD700] mb-2">American Roulette — payouts</h3>
                            <div className="text-xs font-mono space-y-1 text-white/85">
                                <div className="flex justify-between"><span>Straight-up (1 number)</span><b>35:1</b></div>
                                <div className="flex justify-between"><span>Split (2 numbers)</span><b>17:1</b></div>
                                <div className="flex justify-between"><span>Street / Trio (3 numbers)</span><b>11:1</b></div>
                                <div className="flex justify-between"><span>Corner (4 numbers)</span><b>8:1</b></div>
                                <div className="flex justify-between"><span>Top Line — 0, 00, 1, 2, 3</span><b>6:1</b></div>
                                <div className="flex justify-between"><span>Six Line (6 numbers)</span><b>5:1</b></div>
                                <div className="flex justify-between"><span>Column / Dozen (12 numbers)</span><b>2:1</b></div>
                                <div className="flex justify-between"><span>Red / Black · Odd / Even · Low / High</span><b>1:1</b></div>
                            </div>
                            <div className="mt-3 text-xs text-white/70">{cfg.help_text}</div>
                            <div className="mt-3 rounded-lg border border-[#FFD700]/30 bg-black/40 p-2 text-[11px] text-white/80">
                                <div className="font-black uppercase text-[10px] tracking-widest text-[#FFD700] mb-1">Placing combo bets</div>
                                Pick a bet mode above the felt (Split · Street · Trio · Corner · Six-Line · Top Line), then tap the numbers you want to combine. When your selection matches a valid combo, the chip lands automatically. Tap a number again to remove it, or click <b>Cancel selection</b> to start over.
                            </div>
                            <div className="mt-2 text-[10px] uppercase text-white/50">
                                Min ${cfg.min_bet} · Inside max ${cfg.max_inside_bet} · Outside max ${cfg.max_outside_bet} · Table max ${cfg.max_total_wager}/spin
                            </div>
                            <button onClick={() => setRulesOpen(false)} className="mt-3 w-full py-2 rounded-lg bg-[#FFD700] text-black font-black">Got it</button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
